import { useState, useEffect } from "react";
import {
  Card,
  IndexTable,
  BlockStack,
  SkeletonBodyText,
  IndexFilters,
  Page,
  IndexFiltersMode,
  Text,
  Box,
  IndexFiltersProps,
  EmptyState,
} from "@medentic/polaris-react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { IndexTableHeading } from "@medentic/polaris-react/build/ts/src/components/IndexTable/IndexTable";
import { NonEmptyArray } from "@medentic/polaris-react/build/ts/src/types";
import type { Venue } from "@features/events/types";
import { AppPageWrapper } from "@shared/ui";
import { TabsTableSkeleton } from "@shared/ui";
import { useVenues } from "@features/events/hooks/useVenues";
import EventsDefaultAvatar from "@features/events/components/EventsDefaultAvatar";
import { getLocalizedText } from "@shared/lib";
import emptyLocationImage from "@shared/assets/empty-states/empty-location.svg";

export default function EventVenues() {
  const { t } = useTranslation();
  const { eventOrganizerId: hcc } = useParams<{ eventOrganizerId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const serializedSearchParams = searchParams.toString();

  // Tabs for all/my venues
  const tabs = [
    { content: t('venues.list.tabs.all'), index: 0, id: "all" },
    { content: t('venues.list.tabs.my_venues'), index: 1, id: "myVenues" },
  ];

  // Initialize state from URL params
  const [selectedTab, setSelectedTab] = useState(() => {
    const status = searchParams.get("status");
    if (status === "myVenues") return 1;
    return 0;
  });
  const [queryValue, setQueryValue] = useState(
    () => searchParams.get("search") ?? ""
  );
  const [debouncedQuery, setDebouncedQuery] = useState(
    () => searchParams.get("search") ?? ""
  );
  const [sortSelected, setSortSelected] = useState<string[]>(() => {
    const sortParam = searchParams.get("sort");
    if (sortParam) return [sortParam];
    
    // Check localStorage for saved sorting preference
    const savedSort = localStorage.getItem("events_venues_sorting");
    return savedSort ? [savedSort] : ["name asc"];
  });
  const [mode, setMode] = useState(IndexFiltersMode.Default);

  // Sort options
  const sortOptions: IndexFiltersProps['sortOptions'] = [
    {label: t('venues.sort_by.created'), value: 'created asc', directionLabel: t('common.oldestToNewest')},
    {label: t('venues.sort_by.created'), value: 'created desc', directionLabel: t('common.newestToOldest')},
    
    {label: t('venues.sort_by.modified'), value: 'updated asc', directionLabel: t('common.oldestToNewest')},
    {label: t('venues.sort_by.modified'), value: 'updated desc', directionLabel: t('common.newestToOldest')},
    
    {label: t('venues.sort_by.name'), value: 'name asc', directionLabel: t('common.AtoZ')},
    {label: t('venues.sort_by.name'), value: 'name desc', directionLabel: t('common.ZtoA')},
  ];

  // Save sorting preference to localStorage
  useEffect(() => {
    if (sortSelected[0]) {
      localStorage.setItem("events_venues_sorting", sortSelected[0]);
    }
  }, [sortSelected]);

  // Fetch venues
  const { data: venues = [], isLoading, isFetching } = useVenues({
    hcc,
    usedBefore: selectedTab === 1,
    query: debouncedQuery,
    sort: sortSelected[0],
  });

  // Debounce the search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(queryValue);
    }, 500);
    return () => clearTimeout(timer);
  }, [queryValue]);

  // Update URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (selectedTab === 1) params.set("status", "myVenues");
    if (debouncedQuery) params.set("search", debouncedQuery);
    if (sortSelected[0]) params.set("sort", sortSelected[0]);
    setSearchParams(params);
  }, [selectedTab, debouncedQuery, sortSelected, setSearchParams]);

  // Table headings
  const headings: NonEmptyArray<IndexTableHeading> = [
    { id: "image", title: '' },
    { id: "name", title: t('venues.list.column.name') },
    { id: "address", title: t('venues.list.column.address') },
    { id: "city", title: t("venues.list.column.city") },
    { id: "country", title: t("venues.list.column.country") },
  ];

  // Table rows
  const rowMarkup = venues.map((v: Venue, idx: number) => (
    <IndexTable.Row
      id={v.id!}
      key={v.id!}
      position={idx}
      onClick={() =>
        navigate(
          `/e/${hcc}/venues/${v.id}${
            serializedSearchParams ? `?${serializedSearchParams}` : ""
          }`
        )
      }
    >
      <IndexTable.Cell>
        <EventsDefaultAvatar name={v.name} singleImage={v.videoImage} media={v.media} />
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text as="span" variant="bodyMd">
          {getLocalizedText(v.name)}
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Box maxWidth="300px">
          <Text as="span" variant="bodySm" truncate>
            {getLocalizedText(v.geoAddress?.summary)}
          </Text>
        </Box>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text as="span" variant="bodySm">
        {getLocalizedText(v.geoAddress?.city)}
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text as="span" variant="bodySm">
        {getLocalizedText(v.geoAddress?.country)}
        </Text>
      </IndexTable.Cell>
    </IndexTable.Row>
  ));

  const isInitialLoading = isLoading && venues.length === 0;

  if (isInitialLoading) {
    return (
      <AppPageWrapper>
        <Page fullWidth title={t('venues.list.page_title')}>
          <TabsTableSkeleton />
        </Page>
      </AppPageWrapper>
    );
  }

  return (
    <AppPageWrapper>
      <Page
        fullWidth
        title={t('venues.list.page_title')}
        primaryAction={{
          content: t("venues.list.cta.add_location"),
          onAction: () => navigate(`/e/${hcc}/venues/new`),
        }}
      >
        {venues.length === 0 && !isLoading ? (
          <Card>
            <EmptyState
              heading={t('venues.list.emptyState.title')}
              image={emptyLocationImage}
              action={{
                content: t("venues.list.cta.add_location"),
                onAction: () => navigate(`/e/${hcc}/venues/new`),
              }}
            >
              <Text as="p" variant="bodyMd" tone="subdued">
                {t('venues.list.emptyState.description')}
              </Text>
            </EmptyState>
          </Card>
        ) : (
          <Card padding="0">
            <BlockStack gap="0">
              <IndexFilters
                tabs={tabs}
                selected={selectedTab}
                onSelect={setSelectedTab}
                queryValue={queryValue}
                queryPlaceholder={t('venues.list.search_placeholder')}
                onQueryChange={setQueryValue}
                onQueryClear={() => setQueryValue("")}
                filters={[]}
                cancelAction={{
                  onAction: () => {
                    setSelectedTab(0);
                    setQueryValue("");
                    setSortSelected(["name asc"]);
                  },
                  disabled: false,
                  loading: false,
                }}
                appliedFilters={[]}
                onClearAll={() => {
                  setSelectedTab(0);
                  setQueryValue("");
                  setSortSelected(["name asc"]);
                }}
                sortOptions={sortOptions}
                sortSelected={sortSelected}
                onSort={setSortSelected}
                mode={mode}
                setMode={setMode}
                loading={isFetching && venues.length > 0}
              />
              <IndexTable
                resourceName={{
                  singular: t('venues.list.venue'),
                  plural: t('venues.list.venues'),
                }}
                itemCount={venues.length}
                selectable={false}
                headings={headings}
              >
                {isLoading
                  ? [...Array(5)].map((_, i) => (
                      <IndexTable.Row id={`skeleton-${i}`} key={i} position={i}>
                        {headings.map((_, j) => (
                          <IndexTable.Cell key={j}>
                            <SkeletonBodyText lines={1} />
                          </IndexTable.Cell>
                        ))}
                      </IndexTable.Row>
                    ))
                  : rowMarkup}
              </IndexTable>
            </BlockStack>
          </Card>
        )}
      </Page>
    </AppPageWrapper>
  );
}
